import type { AuthData } from '@/context/auth.context/auth.types';
import type { CookieConsent } from '@/types/cookies.types';

import { getPersistenceSetting } from './cookies';
import { applyPosthogEnvironmentCapture } from './posthogCapture';

import type { PostHog } from 'posthog-js';
import type posthogType from 'posthog-js';

let posthogInstance: typeof posthogType | null = null;

/** Safe capture method that waits until PostHog is loaded. */
export async function capture(event: string, properties?: Record<string, unknown>) {
  const ph = posthogInstance || (await loadPosthog());
  ph?.capture(event, properties);
}

/** Safe identify method that waits until PostHog is loaded. */
export async function identify(id: string, properties?: Record<string, unknown>) {
  const ph = posthogInstance || (await loadPosthog());
  ph?.identify(id, properties);
}

/** Dynamically loads PostHog on the client side. */
export async function loadPosthog() {
  if (typeof window === 'undefined') return null; // SSR guard

  if (!posthogInstance) {
    const phModule = await import('posthog-js');
    posthogInstance = phModule.default;

    posthogInstance.init(process.env.NEXT_PUBLIC_POSTHOG_KEY as string, {
      api_host: 'https://eu.i.posthog.com',
      capture_pageview: false,
      enable_heatmaps: true,
      persistence: getPersistenceSetting(),
    });

    applyPosthogEnvironmentCapture(posthogInstance);
  }

  return posthogInstance;
}

/** Returns a ready-to-use PostHog instance if already loaded, else null. */
export function getPosthog() {
  return posthogInstance;
}

const INTERNAL_EMAIL_DOMAIN = '@geofluxus.com';

export function isInternalUser(email?: string) {
  return !!email?.toLowerCase().endsWith(INTERNAL_EMAIL_DOMAIN);
}

/**
 * Manages PostHog analytics tracking behavior based on the authenticated user's data.
 * - Employees (email ending in `@geofluxus.com`) are identified as `internal`, so the anonymous login-flow
 *   recording captured before login is attributed to them and hidden by PostHog's internal-user filter,
 *   then recording stops and capturing is opted out (persisted, so later visits aren't recorded at all).
 * - Other users are identified and associated with their company profile.
 *
 * @param {Object} params - The function parameters.
 * @param {AuthData} params.auth - The authenticated user's data, including user details and company info.
 * @param {PostHog} params.posthog - The PostHog analytics instance used for tracking.
 *
 * @returns {boolean} Returns `true` if PostHog tracking was disabled (employee), otherwise `false`.
 */
export function configurePosthogTracking({ auth, posthog }: { auth: AuthData; posthog: PostHog }) {
  const disablePosthogTracking = isInternalUser(auth?.user?.email);
  if (disablePosthogTracking) {
    // identify is queued before opting out and still flushed, linking the pre-login session to this person
    posthog.identify(`${auth?.user?.id}`, { email: auth?.user?.email, internal: true });
    posthog.stopSessionRecording();
    posthog.opt_out_capturing();
    return disablePosthogTracking;
  }

  // Enables tracking posthog for users that are not employees
  posthog.identify(`${auth?.user?.id}`, {
    email: auth?.user?.email,
    freemium: auth?.user?.freemium,
    group: auth?.company?.id,
    id: auth?.user?.id,
    profile: auth?.company?.name,
    signed: auth?.user?.signed,
    staff: auth?.user?.staff,
    tester: auth?.user?.tester,
  });

  posthog.group('profile', String(auth?.company?.id), {
    name: auth?.company?.name,
    id: auth?.company?.id,
  });

  return disablePosthogTracking;
}

/** Enables or disables PostHog event capturing based on the user's analytics cookie consent. */
export function updatePosthogCapturing(consentSettings: CookieConsent, posthog: PostHog) {
  if (consentSettings?.allowAnalyticsCookies) {
    posthog.opt_in_capturing();
    posthog.set_config({ persistence: 'localStorage+cookie' });
  } else {
    posthog.set_config({ persistence: 'memory' });
    posthog.opt_out_capturing();
  }
}
